import React, { FC, useCallback, useState } from "react"
import { turbine } from "neql"
import { ReactComponent as IconTrash } from "icons/lib/trash.svg"
import { Button, IconDown, Text, Panel, TextViewEdit } from "dash-styled"
import { useNeqlContextProvider } from "../../../../../../../../Hooks/useNeqlContextProvider"
import { useQualifierCardMetric } from "../../../../../../../../Hooks/useQualifierCardMetric"
import { routePaths } from "../../../../../../../../Routes"
import { Confirm } from "../../../../../../../Common/Modal/Confirm"
import { QualifierCardMetric } from "./QualifierCardMetric"
import { TestCardSettings } from "./TestCardSettings"
import { TutorialCardSettings } from "./TutorialCardSettings"
import { NavButton } from "../../../../../../../Common/NavButton/NavButton"
import { TRAINING_CREATING_ID } from "../../../../../../../../Models/API/QualifierModel"
import { TrainingTestVariables } from "../../../../../../../../Hooks/useQualifierTestCreate"
import { TrainingTutorialVariables } from "../../../../../../../../Hooks/useQualifierTutorialCreate"

export interface QualifierCardProps {
    orgUsername: string
    appID: string
    testId: string
    qualifier: turbine.TrainingTest | turbine.TrainingTutorial
    type: turbine.TrainingType.Test | turbine.TrainingType.Tutorial
    totalBaseline: number
    onAddContent?: () => void
    reload: () => void
    reloadWorkers?: () => void
    createOrUpdateTest: (params: TrainingTestVariables) => Promise<boolean>
    isLoadingTest: boolean
    createOrUpdateTutorial: (
        params: TrainingTutorialVariables
    ) => Promise<boolean>
    isLoadingTutorial: boolean
    maxQuestions?: number
}

export const QualifierCard: FC<QualifierCardProps> = ({
    orgUsername,
    appID,
    testId,
    qualifier,
    type,
    totalBaseline,
    reload,
    reloadWorkers,
    createOrUpdateTest,
    isLoadingTest,
    createOrUpdateTutorial,
    isLoadingTutorial,
    maxQuestions
}) => {
    const isCreating = qualifier.id === TRAINING_CREATING_ID

    const [showSettings, setShowSettings] = useState(isCreating)

    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const clientContext = useNeqlContextProvider("turbine")

    const stats = turbine.useFetchTrainingStatisticsQuery({
        variables: {
            trainingId: qualifier.trainingId
        },
        skip: isCreating,
        client: clientContext
    })

    const metrics = useQualifierCardMetric(
        stats.data?.trainingStatistics,
        totalBaseline,
        qualifier.numQuestions?.toString() ?? "N/A",
        type
    )

    const [updateTraining, updateTrainingResult] =
        type === turbine.TrainingType.Test
            ? turbine.useUpdateTrainingTestMutation({
                  client: clientContext
              })
            : turbine.useUpdateTrainingTutorialMutation({
                  client: clientContext
              })

    const [deleteTraining, deleteTrainingResult] =
        type === turbine.TrainingType.Test
            ? turbine.useDeleteTrainingTestMutation({
                  variables: { deleteTrainingTestId: qualifier.id },
                  client: clientContext
              })
            : turbine.useDeleteTrainingTutorialMutation({
                  variables: { deleteTrainingTutorialId: qualifier.id },
                  client: clientContext
              })

    const handleReload = useCallback(() => {
        if (!isCreating) {
            stats.refetch()
        }
        reloadWorkers?.()
    }, [reloadWorkers, stats, isCreating])

    const handleDelete = useCallback(async () => {
        await deleteTraining()
        reload()
    }, [deleteTraining, reload])

    const handleEditTitleDone = useCallback(
        async (updatedTitle: string) => {
            await updateTraining({
                variables: {
                    updateTrainingId: qualifier.id,
                    updateTrainingTitle: updatedTitle
                }
            })

            reload()
        },
        [qualifier.id, reload, updateTraining]
    )

    const handleEditDescriptionDone = useCallback(
        async (updatedDescription: string) => {
            await updateTraining({
                variables: {
                    updateTrainingId: qualifier.id,
                    updateTrainingDescription: updatedDescription
                }
            })
            reload()
        },
        [qualifier.id, reload, updateTraining]
    )

    const renderSettings = () => {
        switch (type) {
            case turbine.TrainingType.Test:
                return (
                    <TestCardSettings
                        appID={appID}
                        qualifier={qualifier as turbine.TrainingTest}
                        showSettings={showSettings}
                        reload={handleReload}
                        isPreparingCreation={isCreating}
                        createOrUpdateTest={createOrUpdateTest}
                        isLoading={isLoadingTest}
                        maxQuestions={maxQuestions}
                    />
                )
            case turbine.TrainingType.Tutorial:
                return (
                    <TutorialCardSettings
                        appID={appID}
                        qualifier={qualifier as turbine.TrainingTutorial}
                        showSettings={showSettings}
                        createOrUpdateTutorial={createOrUpdateTutorial}
                        isLoading={isLoadingTutorial}
                        isPreparingCreation={isCreating}
                    />
                )
            default:
                return <></>
        }
    }

    return (
        <Panel data-testid={`qualifier-card-${testId}`} size="lg" noPadding>
            <div className="bg-white rounded-t-md border-t border-l border-r p-4">
                <div className="flex justify-between">
                    <div className="flex flex-col w-full pr-6">
                        <TextViewEdit
                            id={"title-" + qualifier.id}
                            text={qualifier.taskInfo?.title ?? ""}
                            onFinishEditing={handleEditTitleDone}
                            className="text-lg font-semibold w-full"
                            disabled={
                                updateTrainingResult.loading || isCreating
                            }
                        />
                        <TextViewEdit
                            rows={2}
                            id={"description-" + qualifier.id}
                            text={qualifier.taskInfo?.description ?? ""}
                            onFinishEditing={handleEditDescriptionDone}
                            className="text-sm text-rhino-900"
                            disabled={
                                updateTrainingResult.loading || isCreating
                            }
                            maxLength={250}
                        />
                    </div>
                    {!isCreating && (
                        <div className="flex items-center">
                            {metrics === undefined ? (
                                <Button className="w-max-content h-8 text-sm py-1.5 px-2.5 rounded-lg mr-2.5">
                                    Add content
                                </Button>
                            ) : (
                                <>
                                    <NavButton
                                        className="w-max-content h-8 text-sm py-1.5 px-2.5 rounded-lg text-smoke-610 mr-2.5"
                                        color="white"
                                        to={
                                            type ===
                                            turbine.TrainingType.Tutorial
                                                ? routePaths.appQualifierTutorialContent(
                                                      orgUsername,
                                                      appID,
                                                      qualifier.id
                                                  )
                                                : routePaths.appQualifierTestContent(
                                                      orgUsername,
                                                      appID,
                                                      qualifier.id
                                                  )
                                        }
                                    >
                                        {type === turbine.TrainingType.Tutorial
                                            ? "Edit content"
                                            : "Show content"}
                                    </NavButton>
                                </>
                            )}
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowConfirmDelete(true)}
                            >
                                <IconTrash className="text-smoke-800 w-6 h-6" />
                            </div>
                        </div>
                    )}
                </div>
                {metrics && (
                    <div className="mt-4">
                        <div className="flex justify-around">
                            {metrics.map((metric, index) => (
                                <QualifierCardMetric
                                    key={index}
                                    data={metric}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <div className="bg-rhino-80 rounded-b-md border-b border-l border-r p-4 pt-3 pb-3">
                <div
                    className="flex cursor-pointer items-center"
                    onClick={() => setShowSettings(!showSettings)}
                    data-testid={`qualifier-card-${testId}-settings-header`}
                >
                    <IconDown
                        className={`transform duration-300 ${
                            !showSettings ? "-rotate-90" : ""
                        } mr-2 text-smoke-800`}
                        size="xs"
                    />
                    <Text
                        size="md"
                        weight="semibold"
                        className="text-rhino-900 text-opacity-70 m-0"
                    >
                        Settings
                    </Text>
                </div>
                {renderSettings()}
            </div>
            {showConfirmDelete && (
                <Confirm
                    title={`Are you sure you want to delete this ${type}?`}
                    yesLabel="Delete"
                    noLabel="Cancel"
                    onClickYes={handleDelete}
                    onClickNo={() => setShowConfirmDelete(false)}
                    onClose={() => setShowConfirmDelete(false)}
                    isProcessing={deleteTrainingResult.loading}
                />
            )}
        </Panel>
    )
}
